import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Arg, Entity, Field, Mutation, Query, Subscription } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getCollection, getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { subscribePaginatedAggregation } from '@adornis/baseql/server/resolver/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ConvertConfig, FieldMappingEntry } from '@adornis/import-export/db/convert-config.js';
import { exportCSVFromEntities } from '@adornis/import-export/server/export-internals.js';
import { DateTime } from 'luxon';
import { catchError, from, of, switchMap } from 'rxjs';
import { FundingpotFundingFilter } from '../../../filter/FundingpotFundingFilter.js';
import { KPF } from '../../kpf/entities/KPF.js';
import { PartnerFilter, searchPartnerAggregation } from '../api/subscribePartnerByFilter.js';
import { Fundingpot } from './Fundingpot.js';
import { Partner } from './Partner.js';
import { School } from './School.js';

/**
 * @entity KPFFunding
 */
@Entity()
export class KPFFunding extends AdornisEntity {
  static override _class = 'KPFFunding';

  @Field(() => KPF)
  kpf!: KPF;

  @Field(() => DateTime)
  date!: DateTime;

  @Field(() => Fundingpot)
  fundingIs?: Maybe<Fundingpot>;

  @Field(() => Fundingpot)
  fundingShould1?: Maybe<Fundingpot>;

  @Field(() => Fundingpot)
  fundingShould2?: Maybe<Fundingpot>;

  @Field(() => Fundingpot)
  fundingShould3?: Maybe<Fundingpot>;
}

/**
 * @entity Foerderung
 */
@Entity()
export class FundingpotFunding extends MongoEntity {
  public static override _class = 'FundingpotFunding';
  public static override _collectionName = 'fundingpot-fundings';

  @Field(() => ID)
  public fundingpotID!: string;

  @Field(() => ID)
  public kpfID!: string;

  @DataLoadedField(() => KPF, ({ kpfID }) => kpfID)
  public kpf!: KPF;

  @Field(() => DateTime)
  public month!: DateTime;

  @Field(() => String)
  public type!: 'is' | 'should';

  @Field(() => Int, { default: value => value ?? 0 })
  public position!: number;

  @Subscription(() => [FundingpotFunding])
  static subscribeFundingpotFundingsForKpf(@Arg('kpfID', () => String) kpfID: string) {
    return (gqlFields: BaseQLSelectionSet<FundingpotFunding>) => {
      const selector = { _class: FundingpotFunding._class, kpfID };
      const handle = getCollectionHandle<FundingpotFunding>(FundingpotFunding._collectionName);
      return from(publishFilter(FundingpotFunding._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          return of([] as FundingpotFunding[]);
        }),
      );
    };
  }

  @Subscription(() => [FundingpotFunding])
  static subscribeFundingpotFundingsForFundingpot(@Arg('fundingpotID', () => String) fundingpotID: string) {
    return (gqlFields: BaseQLSelectionSet<FundingpotFunding>) => {
      const selector = { _class: FundingpotFunding._class, kpfID: fundingpotID };
      const handle = getCollectionHandle<FundingpotFunding>(FundingpotFunding._collectionName);
      return from(publishFilter(FundingpotFunding._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          return of([] as FundingpotFunding[]);
        }),
      );
    };
  }

  @Subscription(() => [FundingpotFunding])
  static subscribeFundingpotFundingsPaginatedFiltered(
    @Arg('skip', () => Int) skip: number,
    @Arg('limit', () => Int) limit: number,
    @Arg('filter', () => FundingpotFundingFilter) filter: FundingpotFundingFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<FundingpotFunding>) => {
      return subscribePaginatedAggregation(FundingpotFunding, skip, limit, { fundingpotID: filter.fundingpotID }, [
        ...searchPartnerAggregation(new PartnerFilter({ ...filter })),
        { $sort: filter.getMongoOptions().sort },
      ]);
    };
  }

  @Mutation(() => Int)
  public static updateFundingpotFundingsForKpf(
    @Arg('kpfID', () => String) kpfID: string,
    @Arg('fundings', () => [FundingpotFunding]) fundings: FundingpotFunding[],
  ) {
    return async (gqlFields: void) => {
      const c = await getCollection(FundingpotFunding);
      while (true) {
        const result = await c.findOneAndDelete({ kpfID, _id: { $nin: fundings.map(ft => ft._id) } });
        if (!result) break;
      }

      for (const ft of fundings) {
        try {
          await ft.create();
        } catch (e) {
          await ft.save();
        }
      }
    };
  }

  @Query(() => String)
  static exportFundingsForKpfCSV(@Arg('kpfID', () => ID) kpfID?: string) {
    return async () => {
      const mapPot = (input: string, pf: string) =>
        new FieldMappingEntry({ input }, undefined, (v: any, data: KPFFunding, i: number) => {
          const pot = data[`funding${pf}`];
          if (!pot?.partner) return '';
          return `${pot.partner.name}: ${pot.name}`;
        });

      const convertConfig = new ConvertConfig<KPFFunding>({
        className: KPFFunding._class,
        delimiter: ';',
        fieldMappings: [
          new FieldMappingEntry({ input: 'Monat' }, undefined, (v: any, data: KPFFunding, i: number) =>
            data.date?.toFormat('yyyyLL'),
          ),
          new FieldMappingEntry(
            { input: 'KPF' },
            undefined,
            (v: any, data: KPFFunding, i: number) =>
              `${data.kpf.school?.kuerzel ?? ''}_${data.kpf.produkt?.kuerzel ?? ''}`,
          ),
          mapPot('FördertopfIST', 'Is'),
          mapPot('FördertopfSOLL_1', 'Should1'),
          mapPot('FördertopfSOLL_2', 'Should2'),
          mapPot('FördertopfSOLL_3', 'Should3'),
        ],
      });

      const dataQuery = (await getRawCollection(FundingpotFunding._collectionName)).aggregate<FundingpotFunding>([
        ...(kpfID ? [{ $match: { kpfID } }] : []),
        {
          $group: {
            _id: { $concat: ['$kpfID', '_', { $dateToString: { date: '$month', format: '%Y%m' } }] },
            kpfID: { $first: '$kpfID' },
            date: { $first: '$month' },
            fundingpots: { $push: { type: '$type', id: '$fundingpotID', position: '$position' } },
          },
        },
        {
          $project: {
            _id: 1,
            kpfID: 1,
            date: 1,
            fundingIs: { $filter: { input: '$fundingpots', as: 'pot', cond: { $eq: ['$$pot.type', 'is'] } } },
            fundingShould: {
              $sortArray: {
                input: { $filter: { input: '$fundingpots', as: 'pot', cond: { $eq: ['$$pot.type', 'should'] } } },
                sortBy: { position: 1 },
              },
            },
          },
        },
        {
          $project: {
            _id: 1,
            kpfID: 1,
            date: 1,
            fundingIs: { $arrayElemAt: ['$fundingIs', 0] },
            fundingShould1: { $arrayElemAt: ['$fundingShould', 0] },
            fundingShould2: { $arrayElemAt: ['$fundingShould', 1] },
            fundingShould3: { $arrayElemAt: ['$fundingShould', 2] },
          },
        },
        ...['Is', 'Should1', 'Should2', 'Should3'].map(pf => ({
          $lookup: {
            from: Fundingpot._collectionName,
            as: `funding${pf}`,
            localField: `funding${pf}.id`,
            foreignField: '_id',
          },
        })),
        { $lookup: { from: KPF._collectionName, as: 'kpf', localField: 'kpfID', foreignField: '_id' } },
        { $unwind: '$kpf' },
        {
          $lookup: {
            from: 'dynamic-class-products',
            as: 'kpf.produkt',
            localField: 'kpf.produkt._class',
            foreignField: 'class',
          },
        },
        {
          $lookup: { from: School._collectionName, as: 'kpf.school', localField: 'kpf.schoolID', foreignField: '_id' },
        },
        {
          $project: {
            _class: KPFFunding._class,
            _id: 1,
            kpf: { produkt: { $arrayElemAt: ['$kpf.produkt', 0] }, school: { $arrayElemAt: ['$kpf.school', 0] } },
            date: 1,
            fundingIs: { $arrayElemAt: ['$fundingIs', 0] },
            fundingShould1: { $arrayElemAt: ['$fundingShould1', 0] },
            fundingShould2: { $arrayElemAt: ['$fundingShould2', 0] },
            fundingShould3: { $arrayElemAt: ['$fundingShould3', 0] },
          },
        },
        ...['Is', 'Should1', 'Should2', 'Should3'].map(pf => ({
          $lookup: {
            from: Partner._collectionName,
            as: `funding${pf}.partner`,
            localField: `funding${pf}.partnerID`,
            foreignField: '_id',
          },
        })),
        {
          $project: ['Is', 'Should1', 'Should2', 'Should3']
            .map(pf => ({
              key: `funding${pf}`,
              value: { _class: 1, _id: 1, name: 1, school: 1, partner: { $arrayElemAt: [`$funding${pf}.partner`, 0] } },
            }))
            .reduce((acc, { key, value }) => ({ ...acc, [key]: value }), { _class: 1, _id: 1, kpf: 1, date: 1 }),
        },
      ]);

      const filePath = await exportCSVFromEntities(convertConfig, dataQuery as any, `${kpfID}_foerderungen`);
      const fileName = filePath.split('/').slice(-1)[0];
      if (!fileName) throw new Error('export failed');
      return fileName;
    };
  }
}
